import { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  HomeIcon, 
  CalendarIcon, 
  UserGroupIcon, 
  DocumentTextIcon, 
  CreditCardIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import ThemeSwitcher from './ThemeSwitcher';
import LanguageSwitcher from './LanguageSwitcher';
import { useAuth } from '../hooks/useAuth';
import { hasRole, type RoleName } from '../utils/rbac';

interface NavigationItem {
  key: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  badge?: string;
  roles?: RoleName[];
}

const navigationItems: NavigationItem[] = [
  {
    key: 'dashboard',
    href: '/dashboard',
    icon: HomeIcon,
  },
  {
    key: 'appointments',
    href: '/appointments',
    icon: CalendarIcon,
    badge: '12',
  },
  {
    key: 'patients',
    href: '/patients',
    icon: UserGroupIcon,
  },
  {
    key: 'records',
    href: '/records',
    icon: DocumentTextIcon,
    roles: ['Admin', 'Dentist', 'Assistant'],
  },
  {
    key: 'billing',
    href: '/billing',
    icon: CreditCardIcon,
    badge: '3',
    roles: ['Admin', 'Dentist', 'Receptionist'],
  },
  {
    key: 'team',
    href: '/users',
    icon: UserGroupIcon,
    roles: ['Admin'],
  },
  {
    key: 'settings',
    href: '/settings',
    icon: Cog6ToothIcon,
  },
];

const DEV_MODE = import.meta.env.DEV;

interface SidebarProps {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
}

export default function Sidebar({ isCollapsed, setIsCollapsed }: SidebarProps) {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation(['sidebar', 'common']);
  const isRTL = i18n.dir() === 'rtl';
  const { user } = useAuth();

  const userDisplayName = useMemo(() => {
    if (!user) return 'User';
    const first = user.firstName || '';
    const last = user.lastName || '';
    if (first || last) return `${first} ${last}`.trim();
    return user.email?.split('@')[0] || 'User';
  }, [user]);

  const userInitials = useMemo(() => {
    if (!user) return 'U';
    const first = user.firstName?.[0] || '';
    const last = user.lastName?.[0] || '';
    if (first || last) return `${first}${last}`.toUpperCase();
    return user.email?.[0]?.toUpperCase() || 'U';
  }, [user]);

  const userRole = useMemo(() => {
    if (!user) return t('user.role');
    if (user.isSuperuser) return t('user.roles.admin', { defaultValue: 'Administrator' });
    if (user.role) return user.role;
    return t('user.role');
  }, [user, t]);

  const handleLogout = () => {
    window.location.href = '/accounts/logout/';
  };

  const handleCollapseToggle = () => {
    setIsCollapsed(!isCollapsed);
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 350);
  };

  const NavigationItems = ({ mobile = false }: { mobile?: boolean }) => (
    <ul role="list" className="space-y-0.5">
      {navigationItems.filter((item) => !item.roles || hasRole(user, item.roles)).map((item) => {
        const isActive = location.pathname === item.href ||
          (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
        const IconComponent = item.icon;
        const itemName = t(`navigation.${item.key}`);
        
        return (
          <li key={item.key}>
            <Link
              to={item.href}
              onClick={() => mobile && setIsMobileOpen(false)}
              className={`
                group relative flex items-center px-3 py-2.5 text-sm font-medium rounded-lg
                transition-all duration-150 ease-out
                active:scale-[0.97]
                ${isActive
                  ? 'text-sidebar-text-active bg-sidebar-item-active shadow-sm'
                  : 'text-sidebar-text hover:text-sidebar-text-active hover:bg-sidebar-item-hover hover:scale-[1.02]'
                }
                ${isCollapsed && !mobile ? 'justify-center hover:scale-105' : ''}
              `}
              title={isCollapsed && !mobile ? `${itemName}${item.badge ? ` (${item.badge})` : ''}` : undefined}
            >
              {/* Active indicator bar */}
              {isActive && !isCollapsed && !mobile && (
                <span className={`absolute inset-y-1.5 w-0.5 rounded-full bg-primary ${isRTL ? 'right-0' : 'left-0'}`} />
              )}

              <IconComponent 
                className={`
                  h-5 w-5 flex-shrink-0 transition-all duration-150
                  ${isActive ? 'text-primary' : 'text-sidebar-text group-hover:text-sidebar-text-active'}
                  ${isCollapsed && !mobile ? '' : (isRTL ? 'ml-3' : 'mr-3')}
                `} 
              />
              
              {/* Collapsed badge dot */}
              {isCollapsed && !mobile && item.badge && (
                <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-primary ring-2 ring-[var(--sidebar-bg)]" />
              )}

              {(!isCollapsed || mobile) && (
                <>
                  <span className="font-medium tracking-wide truncate">{itemName}</span>
                  {item.badge && (
                    <span className={`inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 text-xs font-semibold rounded-full bg-primary/15 text-primary ${
                      isRTL ? 'mr-auto' : 'ml-auto'
                    }`}>
                      {item.badge}
                    </span>
                  )}
                </>
              )}
            </Link>
          </li>
        );
      })}
    </ul>
  );

  /* ─── User Profile Card ─── */
  const UserProfileCard = ({ compact = false }: { compact?: boolean }) => {
    const isProfileActive = location.pathname === '/profile';
    
    if (compact) {
      return (
        <Link
          to="/profile"
          className={`group flex items-center justify-center transition-all duration-200 ${
            isProfileActive
              ? 'ring-2 ring-primary ring-offset-2 ring-offset-[var(--sidebar-bg)] rounded-full'
              : ''
          }`}
          title={`${userDisplayName} — ${t('user.viewProfile', { defaultValue: 'View Profile' })}`}
        >
          <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center shadow-md group-hover:shadow-lg group-hover:scale-105 transition-all duration-200">
            <span className="text-white text-xs font-bold tracking-wide">{userInitials}</span>
          </div>
        </Link>
      );
    }

    return (
      <Link
        to="/profile"
        className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
          isProfileActive
            ? 'bg-sidebar-item-active shadow-sm'
            : 'hover:bg-sidebar-item-hover'
        }`}
      >
        <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center shadow-md group-hover:shadow-lg group-hover:scale-105 transition-all duration-200 flex-shrink-0">
          <span className="text-white text-xs font-bold tracking-wide">{userInitials}</span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground truncate">{userDisplayName}</p>
          <p className="text-xs text-muted-foreground truncate">{userRole}</p>
        </div>
        <UserCircleIcon className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex-shrink-0" />
      </Link>
    );
  };

  return (
    <>
      {/* Mobile menu button */}
      <div className={`fixed top-4 z-50 lg:hidden ${isRTL ? 'right-4' : 'left-4'}`}>
        <button
          type="button"
          className="inline-flex items-center justify-center p-2 rounded-xl text-foreground bg-background/80 backdrop-blur-sm border border-border shadow-soft-sm hover:bg-muted/50 transition-all duration-200"
          onClick={() => setIsMobileOpen(true)}
        >
          <span className="sr-only">{t('common:accessibility.openSidebar')}</span>
          <Bars3Icon className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      {/* Mobile menu overlay */}
      {isMobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
            onClick={() => setIsMobileOpen(false)}
          />
          <div className={`fixed inset-y-0 flex w-full max-w-xs flex-col bg-sidebar-bg border-sidebar-border shadow-soft-xl animate-slide-in ${
            isRTL ? 'right-0 border-l' : 'left-0 border-r'
          }`}>
            {/* Mobile header */}
            <div className="flex h-16 flex-shrink-0 items-center justify-between px-6 border-b border-sidebar-border">
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-lg flex items-center justify-center shadow-soft-lg bg-gradient-primary">
                  <span className="text-white font-bold text-sm">D</span>
                </div>
                <span className={`${isRTL ? 'mr-2' : 'ml-2'} text-lg font-bold text-foreground gradient-text`}>
                  {t('common:appName')}
                </span>
              </div>
              <button
                type="button"
                className={`${isRTL ? 'mr-1' : 'ml-1'} flex h-10 w-10 items-center justify-center rounded-xl text-foreground hover:bg-muted/50 transition-colors duration-200`}
                onClick={() => setIsMobileOpen(false)}
              >
                <span className="sr-only">{t('common:accessibility.closeSidebar')}</span>
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            {/* Mobile navigation */}
            <nav className="flex-1 overflow-y-auto py-6 px-4">
              <NavigationItems mobile />
            </nav>

            {/* Mobile footer */}
            <div className="flex-shrink-0 border-t border-sidebar-border p-4 space-y-3">
              <UserProfileCard />
              <div className="flex items-center justify-between px-1">
                <ThemeSwitcher />
                <LanguageSwitcher compact />
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-3 py-2.5 text-sm font-medium text-destructive hover:bg-destructive/10 rounded-xl transition-all duration-200"
              >
                <ArrowRightOnRectangleIcon className={`h-5 w-5 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                {t('user.logout')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className={`hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:z-40 ${
        isRTL ? 'lg:right-0' : 'lg:left-0'
      }`}>
        <div 
          className={`
            flex flex-col h-full bg-sidebar-bg border-sidebar-border shadow-soft-lg backdrop-blur-sm transition-all duration-300 ease-in-out
            ${isRTL ? 'border-l' : 'border-r'}
            ${isCollapsed ? 'w-[68px]' : 'w-64'}
          `}
        >
          {/* Desktop header */}
          <div className={`flex h-16 flex-shrink-0 items-center ${isCollapsed ? 'justify-center px-2' : 'justify-between px-5'} border-b border-sidebar-border relative`}>
            {!isCollapsed && (
              <Link to="/dashboard" className="flex items-center group">
                <div className="h-8 w-8 rounded-lg flex items-center justify-center shadow-soft-lg bg-gradient-primary group-hover:shadow-lg transition-shadow duration-200">
                  <span className="text-white font-bold text-sm">D</span>
                </div>
                <span className={`${isRTL ? 'mr-2' : 'ml-2'} text-lg font-bold gradient-text`}>
                  {t('common:appName')}
                </span>
              </Link>
            )}
            {isCollapsed && (
              <Link to="/dashboard" className="group">
                <div className="h-8 w-8 rounded-lg flex items-center justify-center shadow-soft-lg bg-gradient-primary group-hover:shadow-lg transition-shadow duration-200">
                  <span className="text-white font-bold text-sm">D</span>
                </div>
              </Link>
            )}

            {/* Collapse toggle — pill button at the right/left edge */}
            <div className={`
              ${isCollapsed
                ? `absolute ${isRTL ? '-left-3' : '-right-3'} top-5`
                : `${isRTL ? 'mr-0' : 'ml-auto'}`
              }
            `}>
              <button
                type="button"
                onClick={handleCollapseToggle}
                className={`
                  group/btn flex items-center justify-center
                  transition-all duration-200 ease-out
                  ${isCollapsed
                    ? 'h-6 w-6 rounded-full bg-sidebar-bg border border-sidebar-border shadow-md hover:shadow-lg hover:border-primary/30'
                    : 'h-7 w-7 rounded-lg hover:bg-sidebar-item-hover'
                  }
                `}
                title={isCollapsed ? t('common:accessibility.expandSidebar') : t('common:accessibility.collapseSidebar')}
              >
                <span className="sr-only">
                  {isCollapsed ? t('common:accessibility.expandSidebar') : t('common:accessibility.collapseSidebar')}
                </span>
                <span className={`
                  flex items-center justify-center
                  text-sidebar-text group-hover/btn:text-sidebar-text-active
                  transition-all duration-200
                  ${isCollapsed ? 'h-3 w-3' : 'h-3.5 w-3.5'}
                `}>
                  {isCollapsed ? (
                    isRTL ? <ChevronLeftIcon className="h-full w-full" /> : <ChevronRightIcon className="h-full w-full" />
                  ) : (
                    isRTL ? <ChevronRightIcon className="h-full w-full" /> : <ChevronLeftIcon className="h-full w-full" />
                  )}
                </span>
              </button>
            </div>
          </div>

          {/* Desktop navigation */}
          <nav className={`flex-1 overflow-y-auto overflow-x-hidden py-4 ${isCollapsed ? 'px-2' : 'px-3'}`}>
            <NavigationItems />
            
            {/* Dev section - only in development */}
            {DEV_MODE && !isCollapsed && (
              <div className="mt-6 pt-4 border-t border-sidebar-border">
                <h3 className="px-3 text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mb-2">
                  {t('development.title')}
                </h3>
                <ul className="space-y-0.5">
                  <li>
                    <Link
                      to="/color-test"
                      className={`group flex items-center px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 ${
                        location.pathname === '/color-test'
                          ? 'text-sidebar-text-active bg-sidebar-item-active'
                          : 'text-muted-foreground hover:text-sidebar-text-active hover:bg-sidebar-item-hover'
                      }`}
                    >
                      <span className="truncate">{t('development.colorTest')}</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/theme-test"
                      className={`group flex items-center px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 ${
                        location.pathname === '/theme-test'
                          ? 'text-sidebar-text-active bg-sidebar-item-active'
                          : 'text-muted-foreground hover:text-sidebar-text-active hover:bg-sidebar-item-hover'
                      }`}
                    >
                      <span className="truncate">{t('development.themeTest')}</span>
                    </Link>
                  </li>
                </ul>
              </div>
            )}
          </nav>

          {/* Desktop footer */}
          <div className={`flex-shrink-0 border-t border-sidebar-border ${isCollapsed ? 'p-2' : 'p-3'}`}>
            {!isCollapsed ? (
              <div className="space-y-2">
                <UserProfileCard />
                <div className="flex items-center justify-between px-1 pt-1">
                  <ThemeSwitcher />
                  <LanguageSwitcher compact />
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center px-3 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 rounded-xl transition-all duration-200"
                >
                  <ArrowRightOnRectangleIcon className={`h-5 w-5 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                  {t('user.logout')}
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <UserProfileCard compact />
                <ThemeSwitcher compact />
                <LanguageSwitcher compact />
                <button
                  onClick={handleLogout}
                  className="flex h-8 w-8 items-center justify-center text-destructive hover:bg-destructive/10 rounded-lg transition-all duration-200"
                  title={t('user.logout')}
                >
                  <ArrowRightOnRectangleIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
